import { FC, ReactNode, useEffect, useRef, useState } from 'react';

export enum EReleaseNotesContentType {
  TEXT = 'text',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
}

interface ReleaseNotesItemSelectorProps {
  contentTypes?: EReleaseNotesContentType[];
  onSelect: (type: EReleaseNotesContentType) => void;
}

const getLabelByType = (type: EReleaseNotesContentType) => {
  switch (type) {
    case EReleaseNotesContentType.IMAGE:
      return 'Image';
    case EReleaseNotesContentType.VIDEO:
      return 'Video';
    case EReleaseNotesContentType.AUDIO:
      return 'Audio';
    case EReleaseNotesContentType.TEXT:
    default:
      return 'Text';
  }
};

const getIconByType = (type: EReleaseNotesContentType): ReactNode => {
  switch (type) {
    case EReleaseNotesContentType.IMAGE:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.5" />
          <path d="M21 16l-5-5-8 8" />
        </svg>
      );
    case EReleaseNotesContentType.VIDEO:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <polygon points="10 9 16 12 10 15 10 9" />
        </svg>
      );
    case EReleaseNotesContentType.AUDIO:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <path d="M9 18V5l12-2v13" />
          <circle cx="6" cy="18" r="3" />
          <circle cx="18" cy="16" r="3" />
        </svg>
      );
    case EReleaseNotesContentType.TEXT:
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
          <path d="M4 7V4h16v3" />
          <path d="M12 4v16" />
          <path d="M9 20h6" />
        </svg>
      );
  }
};

export const ReleaseNotesItemSelector: FC<ReleaseNotesItemSelectorProps> = ({
  contentTypes = [EReleaseNotesContentType.TEXT],
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  return (
    <div ref={rootRef} className="flex justify-end overflow-hidden">
      {isOpen ? (
        <ul className="mr-4 flex items-center gap-2">
          {contentTypes.map((type) => (
            <li key={type}>
              <button
                type="button"
                title={getLabelByType(type)}
                aria-label={getLabelByType(type)}
                onClick={() => {
                  onSelect(type);
                  setIsOpen(false);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-800 text-white transition-colors hover:bg-gray-700"
              >
                {getIconByType(type)}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="z-10 bg-white">
        <button
          type="button"
          title={isOpen ? 'Hide content types' : 'Show content types'}
          aria-label={isOpen ? 'Hide content types' : 'Show content types'}
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex h-8 w-8 items-center justify-center rounded-md bg-gray-800 text-white transition-colors hover:bg-gray-700"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
        </button>
      </div>
    </div>
  );
};
